import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { formatMoney } from '@/lib/credit';
import { DEFAULT_STORE_LOGO, storeLogoOr } from '@/lib/defaultImages';
import {
  listClientStoreAccounts,
  findMerchantStoreRecipient,
  transferStoreBalance,
  type ClientStoreAccount,
  type MerchantClient,
} from '@/lib/storeBalance';
import StoreTransferHistory from '@/components/feature/StoreTransferHistory';

/**
 * StoreBalancesTab — espace client « Mes soldes magasin ».
 * Le client voit les fonds qu'il détient chez chaque commerçant et peut
 * en transférer une partie à un autre client de ce même commerce.
 * Les fonds transférés restent utilisables uniquement chez ce commerçant.
 */
export default function StoreBalancesTab() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<ClientStoreAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [historyFor, setHistoryFor] = useState<number | null>(null);

  // Modal de transfert
  const [transferAccount, setTransferAccount] = useState<ClientStoreAccount | null>(null);
  // Destinataire : saisi par l'utilisateur (email ou nom d'utilisateur), jamais pré-proposé.
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipient, setRecipient] = useState<MerchantClient | null>(null);
  const [resolving, setResolving] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const showFeedback = (ok: boolean, msg: string) => {
    setFeedback({ ok, msg });
    setTimeout(() => setFeedback(null), 4500);
  };

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await listClientStoreAccounts(user.id);
      setAccounts(rows);
    } catch {
      setError('Impossible de charger vos soldes magasin.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const openTransfer = (account: ClientStoreAccount) => {
    setTransferAccount(account);
    setRecipientQuery('');
    setRecipient(null);
    setRecipientError(null);
    setAmount('');
  };

  const closeTransfer = () => {
    if (busy) return;
    setTransferAccount(null);
    setRecipientQuery('');
    setRecipient(null);
    setRecipientError(null);
    setAmount('');
  };

  // Résolution du destinataire à la saisie (email ou nom d'utilisateur),
  // uniquement en différé — aucune liste n'est proposée par défaut.
  useEffect(() => {
    if (!transferAccount) return;
    const q = recipientQuery.trim();
    if (q.length < 3) {
      setRecipient(null);
      setRecipientError(null);
      setResolving(false);
      return;
    }
    let cancelled = false;
    setResolving(true);
    const timer = setTimeout(async () => {
      try {
        const found = await findMerchantStoreRecipient(transferAccount.merchantId, q, transferAccount.creditCustomerId);
        if (cancelled) return;
        setRecipient(found);
        setRecipientError(found ? null : "Aucun client de ce commerce ne correspond à cet email ou nom d'utilisateur.");
      } catch {
        if (!cancelled) {
          setRecipient(null);
          setRecipientError('Recherche impossible pour le moment.');
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [recipientQuery, transferAccount]);

  const submitTransfer = async () => {
    if (!user || !transferAccount) return;
    const amt = Number(String(amount).replace(',', '.'));
    if (!Number.isFinite(amt) || amt <= 0) {
      showFeedback(false, 'Montant invalide.');
      return;
    }
    if (amt > transferAccount.store) {
      showFeedback(false, `Solde magasin insuffisant (disponible : ${formatMoney(transferAccount.store, transferAccount.currency)}).`);
      return;
    }
    const target = recipient;
    if (!target) {
      showFeedback(false, "Indiquez l'email ou le nom d'utilisateur du destinataire.");
      return;
    }
    setBusy(true);
    try {
      const res = await transferStoreBalance({
        merchantId: transferAccount.merchantId,
        fromCustomerId: transferAccount.creditCustomerId,
        toCustomerId: target.id,
        amount: amt,
        fromName: user.name || user.user_name || 'Client',
        toName: target.full_name || 'Client',
        actorId: user.id,
        currency: transferAccount.currency,
      });
      if (!res.success) {
        showFeedback(false, res.error || 'Échec du transfert.');
        return;
      }
      showFeedback(
        true,
        `${formatMoney(amt, transferAccount.currency)} transférés à ${target.full_name} (utilisables chez ${transferAccount.merchantName}).`
      );
      closeTransfer();
      setHistoryFor(transferAccount.creditCustomerId);
      await load();
      setHistoryKey((k) => k + 1);
    } catch {
      showFeedback(false, 'Une erreur est survenue.');
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  // Récap consolidé : total détenu chez tous les commerçants (groupé par devise).
  const totalsByCurrency = accounts.reduce<Record<string, number>>((acc, a) => {
    acc[a.currency] = (acc[a.currency] || 0) + a.store;
    return acc;
  }, {});
  const totalCurrencies = Object.keys(totalsByCurrency);
  const merchantsWithBalance = accounts.filter((a) => a.store > 0).length;

  return (
    <div className="max-w-4xl xl:max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Mes soldes magasin</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Les fonds que vous avez déposés chez chaque commerçant. Vous pouvez en transférer une partie à un autre
            client — ces fonds restent utilisables uniquement chez ce même commerce.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-semibold text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap self-start"
        >
          <i className="ri-refresh-line"></i>Actualiser
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-700 text-sm rounded-lg">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={load} className="ml-auto underline cursor-pointer">Réessayer</button>
        </div>
      )}

      {/* Récap consolidé : total de solde magasin détenu chez tous les commerçants */}
      {!loading && accounts.length > 0 && (
        <div className="bg-secondary-500 text-background-50 rounded-xl p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <span className="w-12 h-12 rounded-xl bg-background-50/15 flex items-center justify-center flex-shrink-0">
              <i className="ri-safe-2-line text-2xl"></i>
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-background-50/80">
                Total de solde magasin détenu chez tous les commerçants
              </p>
              <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                {totalCurrencies.length === 0 ? (
                  <span className="text-2xl font-bold font-heading">{formatMoney(0, 'MAD')}</span>
                ) : (
                  totalCurrencies.map((cur) => (
                    <span key={cur} className="text-2xl font-bold font-heading">
                      {formatMoney(totalsByCurrency[cur], cur)}
                    </span>
                  ))
                )}
              </div>
              <p className="text-[11px] text-background-50/80 mt-1.5">
                Réparti sur {merchantsWithBalance} commerce{merchantsWithBalance > 1 ? 's' : ''} · utilisable uniquement chez chaque commerçant.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:flex-col sm:items-end sm:gap-1 text-xs">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50/15 font-medium whitespace-nowrap">
              <i className="ri-store-2-line"></i>
              {accounts.length} commerce{accounts.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/40 rounded-xl text-center px-4">
          <div className="w-14 h-14 rounded-2xl bg-secondary-100 text-secondary-700 flex items-center justify-center mb-4">
            <i className="ri-store-2-line text-2xl"></i>
          </div>
          <h3 className="text-foreground-800 font-semibold text-sm mb-1">Aucun solde magasin</h3>
          <p className="text-foreground-400 text-xs max-w-md">
            Vos soldes apparaîtront ici dès qu&apos;un commerçant liera votre compte Zifek à son carnet et que vous y
            déposerez des fonds.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {accounts.map((account) => {
            const showHistory = historyFor === account.creditCustomerId;
            return (
              <div
                key={account.creditCustomerId}
                className="bg-background-50 border border-background-200/40 rounded-xl overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5">
                  <div className="flex items-center gap-3.5 min-w-0 flex-1">
                    <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img
                        src={storeLogoOr(account.merchantImage)}
                        alt={account.merchantName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          if (img.src !== DEFAULT_STORE_LOGO) img.src = DEFAULT_STORE_LOGO;
                        }}
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-foreground-950 truncate">{account.merchantName}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[11px] font-medium">
                          <i className="ri-store-2-line"></i>Utilisable ici uniquement
                        </span>
                        {account.debt > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[11px] font-medium">
                            Dette : {formatMoney(account.debt, account.currency)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="text-left sm:text-right">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400">Solde magasin</p>
                      <p className="text-xl font-bold font-heading text-secondary-700">
                        {formatMoney(account.store, account.currency)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setHistoryFor(showHistory ? null : account.creditCustomerId)}
                        className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-full text-xs font-medium whitespace-nowrap bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"
                      >
                        <i className="ri-history-line"></i>
                        <span className="hidden sm:inline">Historique</span>
                      </button>
                      <button
                        onClick={() => openTransfer(account)}
                        disabled={account.store <= 0}
                        className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold whitespace-nowrap bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="ri-send-plane-line"></i>Transférer
                      </button>
                    </div>
                  </div>
                </div>

                {showHistory && (
                  <div className="px-4 pb-4">
                    <StoreTransferHistory
                      merchantId={account.merchantId}
                      creditCustomerId={account.creditCustomerId}
                      currency={account.currency}
                      refreshKey={historyKey}
                      className="border-0 bg-background-100/40"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de transfert */}
      {transferAccount && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={closeTransfer} />
          <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
            <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[92vh] overflow-y-auto">
              <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-send-plane-line text-secondary-600"></i>
                  Transférer mon solde magasin
                </h3>
                <button
                  onClick={closeTransfer}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-xs text-foreground-500 leading-relaxed flex items-start gap-2">
                  <i className="ri-information-line text-secondary-600 mt-0.5"></i>
                  <span>
                    Envoyez une partie de votre solde de <strong>{transferAccount.merchantName}</strong> à un autre
                    client. Le destinataire ne pourra l&apos;utiliser que chez ce même commerce.
                  </span>
                </p>

                <div className="flex items-center justify-between text-xs bg-secondary-50 border border-secondary-200/60 rounded-lg px-3 py-2.5">
                  <span className="text-secondary-700">Solde magasin disponible</span>
                  <span className="font-bold text-secondary-800">
                    {formatMoney(transferAccount.store, transferAccount.currency)}
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                    Montant ({transferAccount.currency})
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
                    placeholder="0"
                    autoFocus
                    className="w-full h-12 px-4 rounded-lg border border-background-200/70 bg-background-50 text-lg font-semibold text-foreground-950 focus:outline-none focus:border-secondary-300 focus:ring-1 focus:ring-secondary-300"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Client destinataire</label>
                  <div className="relative">
                    <i className="ri-user-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                    <input
                      type="text"
                      value={recipientQuery}
                      onChange={(e) => setRecipientQuery(e.target.value)}
                      placeholder="Email ou nom d'utilisateur"
                      autoComplete="off"
                      className="w-full h-11 pl-10 pr-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-secondary-300 focus:ring-1 focus:ring-secondary-300"
                    />
                    {resolving && (
                      <i className="ri-loader-4-line animate-spin absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                    )}
                  </div>
                  {recipient && (
                    <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary-50 border border-secondary-200/60">
                      <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      <span className="text-xs text-secondary-800 min-w-0 truncate">
                        Destinataire trouvé : <strong>{recipient.full_name || recipient.username || recipient.email}</strong>
                      </span>
                    </div>
                  )}
                  {!recipient && recipientError && (
                    <p className="mt-2 text-xs text-accent-700 flex items-start gap-1.5">
                      <i className="ri-error-warning-line mt-0.5"></i>{recipientError}
                    </p>
                  )}
                </div>
              </div>
              <div className="p-4 border-t border-background-200/70 flex gap-2">
                <button
                  onClick={closeTransfer}
                  className="flex-1 h-11 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  onClick={submitTransfer}
                  disabled={busy || resolving || !recipient}
                  className="flex-1 h-11 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                  Transférer
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {feedback && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-full text-xs font-medium max-w-[92vw] text-center ${
            feedback.ok ? 'bg-primary-600 text-background-50' : 'bg-accent-600 text-background-50'
          }`}
        >
          {feedback.msg}
        </div>
      )}
    </div>
  );
}


